import { type Container, Graphics, Rectangle } from 'pixi.js';
import type { FallingBlock } from '@/canvas/components/FallingBlock';

/**
 * デバッグ表示フラグ。本番では false に設定する。
 * - showBoundaryBoxes: 境界ボックスの赤枠表示（GameEngine 側の SHOW_BOUNDARY_BOXES と対応）
 * - showCollisionShapes: ブロックの当たり判定形状の赤線表示
 */
export const DEBUG = {
  showBoundaryBoxes: false,
  showCollisionShapes: false,
} as const;

interface BoxParams {
  boxX: number;
  boxY: number;
  boxWidth: number;
  boxHeight: number;
  boxWallThickness: number;
}

/**
 * ゲーム画面背景（透明）を描画し、全体のヒットエリアを設定する。
 */
export const drawBackground = (
  container: Container,
  screenWidth: number,
  screenHeight: number,
): void => {
  const background = new Graphics();
  background
    .rect(0, 0, screenWidth, screenHeight)
    .fill({ color: 0x2c3e50, alpha: 0 });
  background.hitArea = new Rectangle(0, 0, screenWidth, screenHeight);
  container.addChild(background);
};

/**
 * ゲームフィールドのコの字型ボックス（左壁・右壁・底面）を描画する。
 */
export const drawBox = (container: Container, params: BoxParams): void => {
  const { boxX, boxY, boxWidth, boxHeight, boxWallThickness } = params;
  const box = new Graphics();

  box
    .rect(boxX - boxWallThickness, boxY, boxWallThickness, boxHeight)
    .fill(0xffffff);

  box.rect(boxX + boxWidth, boxY, boxWallThickness, boxHeight).fill(0xffffff);

  box
    .rect(boxX, boxY + boxHeight - boxWallThickness, boxWidth, boxWallThickness)
    .fill(0xffffff);

  container.addChild(box);
};

/**
 * アクティブブロックの当たり判定形状をデバッグ用に描画する。
 * DEBUG.showCollisionShapes が true のときのみ GameEngine.update() から呼ぶ。
 */
export const drawDebugCollisions = (
  graphics: Graphics,
  activeBlocks: Set<FallingBlock>,
): void => {
  graphics.clear();

  for (const block of activeBlocks) {
    const matterBody = block.getMatterBody();
    const vertices = matterBody.vertices;

    if (vertices && vertices.length > 0) {
      graphics.moveTo(vertices[0].x, vertices[0].y);
      for (let i = 1; i < vertices.length; i++) {
        graphics.lineTo(vertices[i].x, vertices[i].y);
      }
      graphics.lineTo(vertices[0].x, vertices[0].y);
      graphics.stroke({ width: 2, color: 0xff0000, alpha: 0.7 });
    }
  }
};
